import React, { useState, useEffect, useRef } from 'react';
import { CheckCircle, Clock, HelpCircle, XCircle, Users, Star, User, UserCheck, FileText, Database, Zap, Briefcase, Microscope } from 'lucide-react';
import { Layout } from './shared';

const serviceLevels = [
  {
    id: '3',
    name: 'Full Service',
    subtitle: 'End-to-end digitization service',
    status: 'available',
    features: [
      'Complete project management by DigiTables team',
      'AI processing + human validation by our experts',
      'Data transformed into analysis-ready format (usually long format)',
      'From scanned documents to program-ready datasets'
    ],
    cta: 'Request a Quote',
    highlight: true,
    gradient: 'from-teal-400 to-teal-600',
    description: 'Dedicated project manager handles everything as your single point of contact'
  },
  {
    id: '2',
    name: 'Self-managed',
    subtitle: 'Client-managed with third-party validation',
    status: 'coming-soon',
    features: [
      'AI processing + human validation by partnered vendors',
      'Client manages project workflow and timeline',
      'Quality review by third-party validation partners',
      'Ideal for customers with existing validation processes'
    ],
    cta: 'Coming soon',
    highlight: false,
    gradient: 'from-blue-400 to-blue-600',
    description: 'You stay in control while experts ensure quality'
  },
  {
    id: '1',
    name: 'Rapid digitization',
    subtitle: 'AI-powered rapid digitization',
    status: 'coming-soon',
    features: [
      'AI-only processing with minimal human validation',
      'Quick upload-download workflow',
      'Basic table extraction and digitization',
      'Ideal for simple tables and quick turnaround needs'
    ],
    cta: 'Coming soon',
    highlight: false,
    gradient: 'from-purple-400 to-purple-600',
    description: 'Perfect for straightforward tables that need fast processing'
  },
];

const SolutionPage = ({ openRequestModal }) => {
  const [isPageLoaded, setIsPageLoaded] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const sectionRefs = useRef({});

  // Page load animation
  useEffect(() => {
    const timer = setTimeout(() => setIsPageLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Email subscription handler
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      // Determine the API URL based on environment
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      
      const response = await fetch(`${apiUrl}/api/forms/waitlist`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitMessage('Thanks! We\'ll notify you when Tier 1 (rapid digitization) and Tier 2 (self-managed) are available.');
        setEmail('');
      } else if (response.status === 409) {
        setSubmitMessage(data.message || 'This email is already on the waitlist');
      } else {
        setSubmitMessage(data.error || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      setSubmitMessage('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
      // Clear message after 5 seconds
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <div className={`transition-opacity duration-1000 ease-out ${
      isPageLoaded ? 'opacity-100' : 'opacity-0'
    }`}>
      <Layout openRequestModal={openRequestModal}>
        {/* Hero Section */}
        <section 
          id="hero" 
          ref={(el) => (sectionRefs.current.hero = el)}
          className="px-6 py-20 bg-gradient-to-br from-white via-blue-50 to-sky-200"
        >
          <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${
            visibleSections.has('hero') || isPageLoaded 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h1 className={`text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight transition-all duration-1200 ease-out delay-200 ${
              visibleSections.has('hero') || isPageLoaded 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}>
              Our 
              <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent"> Solutions</span>
            </h1>
            <p className={`text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto transition-all duration-1200 ease-out delay-400 ${
              visibleSections.has('hero') || isPageLoaded 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-4'
            }`}>
              Choose the service level that matches your volume, typical accuracy requirements, and timeline needs.
            </p>
          </div>
        </section>

      {/* Service Levels Section */}
      <section 
        id="service-levels" 
        ref={(el) => (sectionRefs.current.serviceLevels = el)}
        className="px-6 py-20 bg-white"
      >
        <div className="max-w-6xl mx-auto">
          <div className={`text-center mb-16 transition-all duration-1000 ease-out ${
            visibleSections.has('service-levels') 
              ? 'opacity-100 translate-y-0' 
              : 'opacity-0 translate-y-8'
          }`}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Service levels</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Currently offering our premium full service tier, with additional options coming soon.
            </p>
          </div>
          
          {/* Service Comparison Table */}
          <div className="max-w-5xl mx-auto">
            {/* Mobile scroll hint */}
            <div className="md:hidden text-center mb-4">
              <p className="text-sm text-gray-500">← Swipe to compare all tiers →</p>
            </div>
            
            {/* Responsive Comparison Table */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <div className="min-w-[800px]">
                  {/* Table Header - Combined Row with Fixed Heights */}
                  <div className="grid grid-cols-4 border-b border-gray-100">
                <div className="p-4 md:p-6 flex items-center">
                  <div>
                    <h3 className="text-base md:text-lg font-bold text-gray-900 mb-2">Service features</h3>
                    <p className="text-xs md:text-sm text-gray-600">Compare what's included in each tier</p>
                  </div>
                </div>
                {serviceLevels.map((level) => (
                  <div key={level.id} className={`p-3 md:p-6 text-center bg-gradient-to-br ${level.gradient} flex flex-col`}>
                    {/* Status Badge - Fixed Height */}
                    <div className="h-6 md:h-8 flex items-start justify-center mb-2 md:mb-3">
                      {level.status === 'available' ? (
                        <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 shadow-md">
                          <CheckCircle size={12} />
                          Available Now
                        </div>
                      ) : (
                        <div className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold inline-flex items-center gap-1 border border-white/30">
                          <Clock size={12} />
                          Coming Soon
                        </div>
                      )}
                    </div>
                    
                    {/* Tier Number - Fixed Height */}
                    <div className="h-4 mb-2">
                      <div className="text-white/80 text-xs font-semibold">Tier {level.id}</div>
                    </div>
                    
                    {/* Title - Fixed Height */}
                    <div className="h-8 mb-4 flex items-start justify-center">
                      <h3 className="text-xl font-bold text-white">{level.name}</h3>
                    </div>
                    
                    {/* Subtitle - Fixed Height */}
                    <div className="h-10 mb-3 flex items-start justify-center">
                      <p className="text-white/90 text-sm font-medium">{level.subtitle}</p>
                    </div>
                    
                    {/* Description - Fixed Height */}
                    <div className="h-12 flex items-start justify-center">
                      <p className="text-white/80 text-xs leading-relaxed">{level.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Table Body - Features */}
              <div className="divide-y divide-gray-100">
                {/* Create rows for all unique features */}
                {[
                  'Project Management',
                  'AI Processing',
                  'Human Validation', 
                  'Data Format Output',
                  'Typical Accuracy',
                  'Best For'
                ].map((featureCategory, categoryIndex) => (
                  <div key={categoryIndex} className="grid grid-cols-4 hover:bg-gray-50 transition-colors">
                    <div className="p-2 md:p-4 border-r border-gray-100 flex items-center">
                      <span className="font-medium text-gray-900 text-sm md:text-base">{featureCategory}</span>
                    </div>
                    {serviceLevels.map((level, levelIndex) => (
                      <div key={levelIndex} className="p-2 md:p-4 text-center">
                        {categoryIndex === 0 && (
                          <div className="flex flex-col items-center gap-1">
                            {levelIndex === 0 && <UserCheck className="text-green-500" size={16} />}
                            {levelIndex === 1 && <User className="text-blue-500" size={16} />}
                            {levelIndex === 2 && <User className="text-blue-500" size={16} />}
                            <span className="text-xs text-gray-500">
                              {levelIndex === 0 ? 'Dedicated PM' : levelIndex === 1 ? 'Client-managed' : 'Client-managed'}
                            </span>
                          </div>
                        )}
                        {categoryIndex === 1 && (
                          <div className="flex flex-col items-center gap-1">
                            {levelIndex === 0 && <CheckCircle className="text-green-500" size={16} />}
                            {(levelIndex === 1 || levelIndex === 2) && <CheckCircle className="text-blue-500" size={16} />}
                            <span className="text-xs text-gray-500">
                              {levelIndex === 0 ? 'Multi-step AI' : 'Standard AI'}
                            </span>
                          </div>
                        )}
                        {categoryIndex === 2 && (
                          <div className="flex flex-col items-center gap-1">
                            {levelIndex === 0 && <Star className="text-yellow-500" size={16} />}
                            {levelIndex === 1 && <Users className="text-blue-500" size={16} />}
                            {levelIndex === 2 && <XCircle className="text-red-500" size={16} />}
                            <span className="text-xs text-gray-500">
                              {levelIndex === 0 ? 'Data scientist team' : levelIndex === 1 ? 'Third-party' : 'No'}
                            </span>
                          </div>
                        )}
                        {categoryIndex === 3 && (
                          <div className="flex flex-col items-center gap-1">
                            {levelIndex === 0 && <Database className="text-green-500" size={16} />}
                            {(levelIndex === 1 || levelIndex === 2) && <FileText className="text-gray-500" size={16} />}
                            <span className="text-xs text-gray-500">
                              {levelIndex === 0 ? 'Structured data' : 'Raw extraction'}
                            </span>
                          </div>
                        )}
                        {categoryIndex === 4 && (
                          <div className="flex flex-col items-center gap-1">
                            <div className="flex items-center gap-1">
                              <span className={`text-lg font-bold ${
                                levelIndex === 0 ? 'text-green-600' : 
                                levelIndex === 1 ? 'text-blue-600' : 
                                'text-purple-600'
                              }`}>
                                {levelIndex === 0 ? '99.9%' : levelIndex === 1 ? '95%' : '85%'}
                              </span>
                            </div>
                            <span className="text-xs text-gray-500">Typical Accuracy</span>
                          </div>
                        )}
                        {categoryIndex === 5 && (
                          <div className="flex flex-col items-center gap-1">
                            {levelIndex === 0 && <Microscope className="text-green-500" size={16} />}
                            {levelIndex === 1 && <Briefcase className="text-blue-500" size={16} />}
                            {levelIndex === 2 && <Zap className="text-yellow-500" size={16} />}
                            <span className="text-xs text-gray-500">
                              {levelIndex === 0 ? 'Research & analysis' : levelIndex === 1 ? 'High accuracy' : 'Quick digitization'}
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {/* Table Footer - CTAs */}
              <div className="grid grid-cols-4 border-t border-gray-100 bg-gray-50">
                <div className="p-3 md:p-6"></div>
                {serviceLevels.map((level) => (
                  <div key={level.id} className="p-3 md:p-6 text-center">
                    <button
                      onClick={level.status === 'available' ? openRequestModal : undefined}
                      disabled={level.status !== 'available'}
                      className={`w-full py-2 md:py-3 px-3 md:px-4 rounded-lg text-sm md:text-base font-medium transition-all duration-300 ${
                        level.status === 'available'
                          ? `bg-gradient-to-r ${level.gradient} text-white hover:opacity-95 shadow-sm hover:shadow-md`
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                    >
                      {level.cta}
                    </button>
                  </div>
                ))}
              </div>
                </div>
              </div>
            </div>

          </div>



          {/* Note about availability */}
          <div 
            id="availability-note" 
            ref={(el) => (sectionRefs.current.availabilityNote = el)}
            className={`mt-12 text-center transition-all duration-1000 ease-out ${
              visibleSections.has('availability-note') 
                ? 'opacity-100 translate-y-0' 
                : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="bg-blue-50 rounded-2xl p-6 max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-2 mb-2">
                <HelpCircle className="text-blue-600" size={20} />
                <h3 className="font-semibold text-blue-900">Why full service only?</h3>
              </div>
              <p className="text-blue-800 text-sm leading-relaxed">
                We're currently focused on our full service tier to ensure 99.9% accuracy while we are finalizing our automated AI pipeline. If you are interested in these other solutions, let us know and we will inform you once our Tier 1 (rapid digitization) and Tier 2 (self-managed) are available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Subscription Section */}
      <section 
        id="newsletter-signup" 
        ref={(el) => (sectionRefs.current.newsletterSignup = el)}
        className="px-6 py-20 bg-white"
      >
        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${
          visibleSections.has('newsletter-signup') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}>
          <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8 max-w-2xl mx-auto border border-teal-100">
            <div className="mb-6">
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Stay in the loop</h3>
              <p className="text-gray-700 leading-relaxed">
                Be the first to know when our Tier 1 (rapid digitization) and Tier 2 (self-managed) are ready. 
                Plus, get updates on new features and improvements to our service.
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                  disabled={isSubmitting}
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-6 py-3 bg-gradient-to-r from-teal-600 to-blue-600 text-white rounded-lg font-semibold hover:from-teal-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                >
                  {isSubmitting ? 'Subscribing...' : 'Join waitlist'}
                </button>
              </div>
            </form>

            {submitMessage && (
              <div className={`mt-4 p-3 rounded-lg text-sm font-medium ${
                submitMessage.includes('Thanks') 
                  ? 'bg-green-100 text-green-800 border border-green-200' 
                  : 'bg-red-100 text-red-800 border border-red-200'
              }`}>
                {submitMessage}
              </div>
            )}

            <p className="text-xs text-gray-500 mt-4">
              We respect your privacy. Unsubscribe at any time.
            </p>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section 
        id="cta" 
        ref={(el) => (sectionRefs.current.cta = el)}
        className="px-6 py-20 bg-gray-50"
      >
        <div className={`max-w-4xl mx-auto text-center transition-all duration-1000 ease-out ${
          visibleSections.has('cta') 
            ? 'opacity-100 translate-y-0' 
            : 'opacity-0 translate-y-8'
        }`}>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Ready to get started?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Contact us to discuss your specific needs and get a custom quote for your project.
          </p>
          
          <div className="flex justify-center">
            <button 
              onClick={openRequestModal}
              className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Request a quote
              <svg className="ml-2 group-hover:translate-x-1 transition-transform" width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      </section>

      
      </Layout>
    </div>
  );
};

export default SolutionPage;
